export const en = {
  app: {
    title: 'Stream Dashboard',
    skip: 'Skip to content',
    nav: { live: 'Live feed', connection: 'Connection', settings: 'Settings' },
    navLabel: 'Primary',
  },
  status: {
    connected: 'Connected',
    reconnecting: 'Reconnecting',
    offline: 'Offline',
    stale: 'Stale',
    announce: 'Connection {status}',
  },
  quality: { good: 'Good', suspect: 'Suspect', bad: 'Bad', all: 'All qualities' },
  playback: {
    legend: 'Playback',
    live: 'Live',
    '1x': '1×',
    '10x': '10×',
    '100x': '100×',
    step: 'Step',
    stepOnce: 'Next batch',
    pause: 'Pause',
    resume: 'Resume',
    rate: 'Events per second',
    position: '{position} of {total} batches',
  },
  feed: {
    title: 'Live feed',
    search: 'Filter by name',
    searchPlaceholder: 'e.g. TMP-00042',
    qualityFilter: 'Quality',
    table: 'Instruments',
    columns: {
      name: 'Name',
      kind: 'Kind',
      value: 'Value',
      trend: 'Trend',
      quality: 'Quality',
      updated: 'Updated',
      updates: 'Updates',
    },
    sortBy: 'Sort by {column}',
    noReading: 'No reading yet',
    trendUp: 'Rising',
    trendDown: 'Falling',
    trendFlat: 'Unchanged',
    empty: 'No instruments match the filter.',
    open: 'Open {name}',
  },
  stats: {
    eventsPerSecond: 'Events/s',
    commitsPerSecond: 'Commits/s',
    lastBatch: 'Last batch',
    maxBatch: 'Largest batch',
    dropped: 'Dropped',
    rendered: 'Rows in DOM',
    total: 'Instruments',
    rejected: 'Rejected',
    help: 'One commit per animation frame, however many events arrived in between.',
  },
  kind: {
    temperature: 'Temperature',
    pressure: 'Pressure',
    flow: 'Flow',
    vibration: 'Vibration',
    humidity: 'Humidity',
  },
  instrument: {
    back: 'Back to the live feed',
    previous: 'Previous instrument',
    next: 'Next instrument',
    current: 'Current reading',
    lastUpdate: 'Last update',
    updates: 'Readings received',
    history: 'Last {count} committed values, sampled once per frame',
    chart: '{name}: {count} points, from {min} to {max} {unit}, latest {last} {unit}',
    chartEmpty: '{name}: no readings yet',
    notFound: 'There is no instrument {id} in a fleet of {count}.',
    navLabel: 'Neighbouring instruments',
  },
  connection: {
    title: 'Connection',
    transport: 'Transport',
    transports: { worker: 'Web Worker', replay: 'Recording', none: 'None' },
    mode: 'Mode',
    lastMessage: 'Last data',
    never: 'never',
    ago: '{seconds} s ago',
    faults: 'Fault injection',
    faultsHelp:
      'Live mode only. A stall keeps the connection open and silent; a drop closes it.',
    stall: 'Stall',
    drop: 'Drop',
    clear: 'Clear fault',
    reconnect: 'Reconnect',
    recording: 'Recording',
    recordingHelp:
      'Capture the frames as they arrive, then replay them or attach them to a bug report.',
    startRecording: 'Start recording',
    stopRecording: 'Stop and download',
    recordingFrames: '{count} frames recorded',
    load: 'Load a recording',
    loadHelp: 'A JSON file saved from this page. Playback switches to 1× when it loads.',
    loadError: 'That file is not a recording: {message}',
    loaded: 'Loaded {count} frames from {name}',
    normalizer: 'Normalizer',
    accepted: 'Accepted',
    malformed: 'Malformed',
    duplicate: 'Duplicate',
    stale: 'Stale',
    reordered: 'Reordered',
    gaps: 'Gaps',
    unknownInstrument: 'Unknown instrument',
  },
  settings: {
    title: 'Settings',
    appearance: 'Appearance',
    theme: { legend: 'Theme', system: 'System', light: 'Light', dark: 'Dark' },
    language: { legend: 'Language', en: 'English', ru: 'Русский' },
    fleet: 'Fleet',
    instruments: 'Instruments',
    instrumentsHelp: 'Changing the fleet discards every reading.',
    rate: 'Events per second',
    seed: 'Seed',
    seedHelp: 'Same seed, same stream. Change it to see a different fleet.',
    dirtyWire: 'Dirty wire',
    dirtyWireHelp:
      'Inject duplicates, reorders, NaN and unknown quality codes so the normalizer has something to reject.',
    apply: 'Apply fleet settings',
  },
  notFound: { title: 'Page not found', body: 'Nothing lives at this address.' },
};

/**
 * The shape every locale must fill. Derived from English — not `as const`, so
 * the type is the keys, not the English words.
 */
export type MessageSchema = typeof en;
